// components/units/UnitViewingsCard.tsx — when this flat can be viewed (docs/plans/viewing-first-onboarding.md, phase 2):
// the viewing days, the hours, the slot length, how many viewings fit in one slot and the days off. The website, the app
// and staff book from the free slots these make (GET /units/:id/viewing-slots). Reading needs viewings.read; saving
// viewings.update or units.update. Until saved, the flat uses the defaults (Mon–Fri, 09:00–17:00, 30 minutes, 1 per slot).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Badge, Button, Card, Field, Input, Select, SkeletonRows } from '../ui';
import { FormError } from '../forms/common';
import { useCan } from '../../hooks/usePermission';
import { fmtDay } from '../../lib/dates';
import { todayIso } from '../../lib/applicationForm';
import { DAY_LABELS, HALF_HOURS, SLOT_LENGTHS, VIEWING_DAYS, viewingSettingsApi, type ViewingSettingsInput } from '../../lib/viewingSettings';

export function UnitViewingsCard({ unitId }: { unitId: string }) {
  const canRead = useCan('viewings.read');
  const canUpdateViewings = useCan('viewings.update'), canUpdateUnits = useCan('units.update');
  const canEdit = canUpdateViewings || canUpdateUnits;
  const qc = useQueryClient();
  const key = ['/units', unitId, 'viewing-settings'];
  const q = useQuery({ queryKey: key, queryFn: () => viewingSettingsApi.get(unitId), enabled: canRead });
  // The edits in progress; null = showing what is saved.
  const [draft, setDraft] = useState<ViewingSettingsInput | null>(null);
  const [dayOff, setDayOff] = useState('');
  const [saved, setSaved] = useState(false);
  const d = q.data;
  const s: ViewingSettingsInput | null = draft ?? (d ? { days: d.days, startTime: d.startTime, endTime: d.endTime, slotMinutes: d.slotMinutes, capacity: d.capacity, daysOff: d.daysOff } : null);
  const save = useMutation({
    mutationFn: (body: ViewingSettingsInput) => viewingSettingsApi.put(unitId, body),
    onSuccess: (res) => { qc.setQueryData(key, res); void qc.invalidateQueries({ queryKey: ['/units', unitId, 'viewing-slots'] }); setDraft(null); setSaved(true); },
  });
  if (!canRead) return null;

  const set = (p: Partial<ViewingSettingsInput>) => { setSaved(false); if (s) setDraft({ ...s, ...p }); };
  const toggle = (d: (typeof VIEWING_DAYS)[number]) => s && set({ days: s.days.includes(d) ? s.days.filter((x) => x !== d) : VIEWING_DAYS.filter((x) => x === d || s.days.includes(x)) });
  const addDayOff = () => { if (s && dayOff && !s.daysOff.includes(dayOff)) set({ daysOff: [...s.daysOff, dayOff].sort() }); setDayOff(''); };
  const upcomingOff = s ? s.daysOff.filter((d) => d >= todayIso()) : [];
  const problem = !s ? null : !s.days.length ? 'Pick at least one viewing day' : s.endTime <= s.startTime ? 'The end time must be after the start time' : null;

  return (
    <Card title="Viewings" subtitle="When this flat can be viewed — the website, the app and the team book from these slots"
      actions={q.data && (q.data.isDefault ? <Badge value="Using defaults" tone="neutral" /> : q.data.updatedByName ? <span className="text-[11.5px] text-ink-400">Saved by {q.data.updatedByName}</span> : undefined)}>
      {!s ? <SkeletonRows rows={3} cols={2} /> : (
        <fieldset disabled={!canEdit} className="space-y-3.5">
          <div>
            <span className="mb-1 block text-[12px] font-medium text-ink-500">Viewing days</span>
            <div className="flex flex-wrap gap-1.5">
              {VIEWING_DAYS.map((d) => (
                <button key={d} type="button" onClick={() => toggle(d)} aria-pressed={s.days.includes(d)}
                  className={`rounded-lg border px-3 py-1.5 text-[12.5px] font-medium transition disabled:cursor-default ${s.days.includes(d) ? 'border-bronze-500 bg-bronze-50 text-bronze-700' : 'border-ink-200 bg-white text-ink-500 hover:border-ink-300'}`}>
                  {DAY_LABELS[d]}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Field label="From"><Select value={s.startTime} onChange={(e) => set({ startTime: e.target.value })} className="w-full">{HALF_HOURS.map((h) => <option key={h} value={h}>{h}</option>)}</Select></Field>
            <Field label="Until"><Select value={s.endTime} onChange={(e) => set({ endTime: e.target.value })} className="w-full">{HALF_HOURS.map((h) => <option key={h} value={h}>{h}</option>)}</Select></Field>
            <Field label="Slot length"><Select value={s.slotMinutes} onChange={(e) => set({ slotMinutes: Number(e.target.value) })} className="w-full">{SLOT_LENGTHS.map((m) => <option key={m} value={m}>{m} minutes</option>)}</Select></Field>
            <Field label="Viewings per slot"><Input type="number" min={1} max={20} value={s.capacity} onChange={(e) => set({ capacity: Math.max(1, Math.min(20, Number(e.target.value) || 1)) })} className="w-full" /></Field>
          </div>
          <div>
            <span className="mb-1 block text-[12px] font-medium text-ink-500">Days off</span>
            <div className="flex flex-wrap items-center gap-2">
              <Input type="date" min={todayIso()} value={dayOff} onChange={(e) => setDayOff(e.target.value)} className="w-44" aria-label="Day off" />
              <Button size="sm" variant="secondary" onClick={addDayOff} disabled={!dayOff}>Add day off</Button>
            </div>
            {upcomingOff.length > 0 ? (
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {upcomingOff.map((d) => (
                  <li key={d} className="inline-flex items-center gap-1 rounded-full border border-ink-200 bg-white py-0.5 pl-2.5 pr-1 text-[12px] text-ink-700">
                    {fmtDay(d)}
                    {canEdit && <button type="button" onClick={() => set({ daysOff: s.daysOff.filter((x) => x !== d) })} className="rounded-full p-0.5 text-ink-400 hover:bg-ink-100 hover:text-ink-700" aria-label={`Remove ${d}`}><X className="h-3 w-3" /></button>}
                  </li>
                ))}
              </ul>
            ) : <p className="mt-1.5 text-[11.5px] text-ink-400">No days off coming up.</p>}
          </div>
          {problem && <p className="text-[12px] text-red-600">{problem}</p>}
          <FormError error={save.error} />
          {canEdit && (
            <div className="flex items-center justify-end gap-3">
              {saved && <span className="text-[12px] text-emerald-700">Saved</span>}
              <Button onClick={() => save.mutate({ ...s, daysOff: s.daysOff.filter((d) => d >= todayIso()) })} disabled={!!problem || save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</Button>
            </div>
          )}
        </fieldset>
      )}
    </Card>
  );
}
